import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import StageBadge from "@/components/StageBadge";
import { deliveryBadge, BADGE_STYLES } from "@/lib/orderFields";
import { Pulse, CaretRight } from "@phosphor-icons/react";

/**
 * Live activity feed widget — derives recent scan events from the items'
 * `progress[]` arrays already returned by /api/orders. No new backend
 * endpoint needed.
 */
export default function ActivityFeed({ orders, limit = 12 }) {
  const events = useMemo(() => {
    const out = [];
    (orders || []).forEach((order) => {
      (order.items || []).forEach((item) => {
        (item.progress || []).forEach((step) => {
          if (step.last_timestamp && step.completed) {
            out.push({
              key: `${item.id}-${step.step}`,
              orderId: order.id,
              orderNumber: order.order_number,
              customer: order.customer_name,
              deliveryDate: order.delivery_date,
              sku: item.sku,
              itemName: item.name,
              step: step.step,
              userName: step.last_user_name || "—",
              at: step.last_timestamp,
            });
          }
        });
      });
    });
    out.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
    return out.slice(0, limit);
  }, [orders, limit]);

  return (
    <Card className="border-zinc-200 dark:border-zinc-700 overflow-hidden" data-testid="activity-feed">
      <div className="px-5 py-3 border-b border-zinc-200 dark:border-zinc-700 flex items-center justify-between bg-zinc-50 dark:bg-zinc-800/50">
        <div className="flex items-center gap-2">
          <Pulse size={18} weight="bold" className="text-[#C8102E]" />
          <h2 className="font-heading font-bold text-base">Recent activity</h2>
        </div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">
          last {events.length} events
        </div>
      </div>

      {events.length === 0 ? (
        <div className="p-6 text-center text-zinc-500 text-sm" data-testid="activity-empty">
          No recent activity in this mode.
        </div>
      ) : (
        <div className="divide-y divide-zinc-200 dark:divide-zinc-700">
          {events.map((e) => {
            const urgent = e.deliveryDate ? deliveryBadge(e.deliveryDate) : null;
            const showFlag = urgent && (urgent.tone === "today" || urgent.tone === "overdue");
            return (
              <Link
                key={e.key}
                to={`/orders/${e.orderId}`}
                className="px-5 py-2.5 flex items-center gap-3 hover:bg-zinc-50 dark:hover:bg-zinc-800/60 transition-colors"
                data-testid={`activity-${e.key}`}
              >
                <StageBadge step={e.step} size="sm" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate flex items-center gap-1.5">
                    {e.userName} <span className="font-normal text-zinc-500">advanced</span>{" "}
                    <span className="font-mono">{e.sku}</span> · {e.orderNumber}
                    {showFlag && (
                      <span
                        data-testid={`activity-urgency-${e.key}`}
                        className="ml-1 inline-flex items-center rounded border px-1.5 py-0 text-[9px] font-bold uppercase tracking-[0.15em] whitespace-nowrap"
                        style={{
                          background: BADGE_STYLES[urgent.tone].bg,
                          color: BADGE_STYLES[urgent.tone].text,
                          borderColor: BADGE_STYLES[urgent.tone].border,
                        }}
                      >
                        {urgent.label}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 truncate">
                    {e.itemName} · {e.customer}
                  </div>
                </div>
                <div className="text-[11px] text-zinc-400 font-mono whitespace-nowrap">
                  {relativeTime(e.at)}
                </div>
                <CaretRight size={14} className="text-zinc-300" />
              </Link>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function relativeTime(iso) {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}
